// Workshops page for KATA Dashboard

import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Settings, Eye, Edit, Trash2, Check } from "lucide-react";
import DateRangeFilter from "../components/common/DateRangeFilter";
import {
  useWorkshopStore,
  useWorkshops,
  useWorkshopFilters,
} from "../stores/workshopStore";
import { WorkshopService } from "../services/workshop";
import { DepartmentService } from "../services/department";
import { UserService, User } from "../services/user";
import { ColumnPreferencesService } from "../services/columnPreferences";
import { useIsAdmin } from "../stores/authStore";
import Alert from "../components/common/Alert";
import EditableColumnHeader from "../components/common/EditableColumnHeader";
import ColumnPreferencesModal from "../components/common/ColumnPreferencesModal";
import EditableCell, { WorkshopEditabilityProvider } from "../components/common/EditableCell";
import EditableCurrencyCell from "../components/common/EditableCurrencyCell";
import { ColumnDefinition, ColumnPreferencesResponse } from "../types/api";
import EnhancedPagination from "../components/common/EnhancedPagination";
import { getGroupedOptions, GroupedOptions } from "../services/workshopOptions";

const Workshops: React.FC = () => {
  const { fetchWorkshops, setFilters, clearFilters, setPage, setPageSize } =
    useWorkshopStore();
  const { workshops, isLoading, error, totalCount, hasNext, hasPrevious } =
    useWorkshops();
  const { filters } = useWorkshopFilters();
  const isAdmin = useIsAdmin();
  
  const [departments, setDepartments] = useState<
    Array<{ id: number; name: string; code: string }>
  >([]);
  const [users, setUsers] = useState<User[]>([]);
  const [usersLoaded, setUsersLoaded] = useState(false);
  const [searchInput, setSearchInput] = useState(filters.search || "");
  const [dropdownOptions, setDropdownOptions] = useState<GroupedOptions>({});
  const [deleteAlert, setDeleteAlert] = useState<{
    isOpen: boolean;
    workshopId: number | null;
    workshopTitle: string;
  }>({
    isOpen: false,
    workshopId: null,
    workshopTitle: "",
  });

  // Column visibility state
  const defaultColumns = {
    title: true,
    status: true,
    progress: true,
    department: true,
    mentor: true,
    deadline: true,
    startupDate: false,
    estimatedSavings: true,
    releasedSavings: true,
    savingsRealizationRate: false,
    kpi: false,
    initialCondition: false,
    currentCondition: false,
    targetCondition: false,
    sqdcmCategory: false,
    mentees: false,
    // New fields from Excel analysis
    productLine: false,
    alvId: false,
    location: false,
    category: false,
    ebitBridgeLever: false,
    impactType: false,
    workstream: false,
    apsMaturity: false,
    wsArea: false,
    lGate: false,
    priority: false,
    description: false,
    actions: true,
  };

  type ColumnVisibility = typeof defaultColumns;

  const [visibleColumns, setVisibleColumns] = useState<ColumnVisibility>(() => {
    const saved = localStorage.getItem("workshopTableColumns");
    return saved ? { ...defaultColumns, ...JSON.parse(saved) } : defaultColumns;
  });

  const [showColumnMenu, setShowColumnMenu] = useState(false);
  const [showPreferencesModal, setShowPreferencesModal] = useState(false);

  // Column preferences state
  const [columnPreferences, setColumnPreferences] =
    useState<ColumnPreferencesResponse>({});
  const [columnDefinitions, setColumnDefinitions] = useState<
    ColumnDefinition[]
  >([]);

  // Load initial data
  useEffect(() => {
    fetchWorkshops();
    loadDepartments();
    if (isAdmin) {
      loadUsers();
    } else {
      setUsersLoaded(true);
    }
    loadColumnPreferences();
    loadDropdownOptions();
  }, [fetchWorkshops, isAdmin]);

  // Load departments for filter dropdown
  const loadDepartments = async () => {
    try {
      const deptOptions = await DepartmentService.getDepartmentOptions();
      setDepartments(deptOptions);
    } catch (error) {
      console.error("Failed to load departments:", error);
    }
  };

  // Load users for mentor/mentees display
  const loadUsers = async () => {
    try {
      const fetchedUsers = await UserService.getAllUsers();
      setUsers(fetchedUsers);
      setUsersLoaded(true);
    } catch (error) {
      console.error("Failed to load users:", error);
      setUsersLoaded(true);
    }
  };

  // Load dropdown options for filters
  const loadDropdownOptions = async () => {
    try {
      const options = await getGroupedOptions();
      setDropdownOptions(options);
    } catch (error) {
      console.error("Failed to load dropdown options:", error);
      // Continue with empty options - filters will still work
    }
  };

  // Helper function to get select options for EditableCell
  const getSelectOptions = (attributeName: string, includeEmpty: boolean = false) => {
    const options = dropdownOptions[attributeName] || [];
    const selectOpts = options.map((opt: { value: string; label: string; code?: string }) => ({
      value: opt.value,
      // Format label with code if available (e.g., "213100 - Waste and scrap")
      label: opt.code ? `${opt.code} - ${opt.label}` : opt.label
    }));
    
    if (includeEmpty) {
      return [{ value: "", label: `Select ${attributeName}...` }, ...selectOpts];
    }
    return selectOpts;
  };

  // Helper function to create a display formatter for select fields
  // This looks up the value in the select options and returns the formatted label
  const getDisplayFormatter = (attributeName: string) => {
    return (value: any) => {
      if (!value) return '';
      const options = getSelectOptions(attributeName);
      const option = options.find((opt: { value: string; label: string }) => opt.value === value);
      return option ? option.label : value.toString();
    };
  };

  // Helper function to get user name from ID or return name if already a string
  const getUserName = (userIdOrName: string | number): string => {
    if (!userIdOrName) return '';
    const idOrName = userIdOrName.toString();
    
    // If it's a numeric ID, look up the user
    if (/^\d+$/.test(idOrName)) {
      if (!Array.isArray(users) || users.length === 0) {
        return usersLoaded ? idOrName : `Loading... (${idOrName})`;
      }
      const user = users.find(u => u.id === Number(idOrName));
      if (user) {
        const fullName = `${user.first_name} ${user.last_name}`.trim();
        return fullName || user.username || idOrName;
      }
      return idOrName;
    }
    
    // If it's already a name, return as is
    return idOrName;
  };

  // Helper function to format mentees array (handle both IDs and names)
  const formatMentees = (mentees: string[] | number[] | null, resolvedNames?: string[]): string => {
    if (Array.isArray(resolvedNames) && resolvedNames.length > 0) {
      return resolvedNames.filter(Boolean).join(', ');
    }
    if (!Array.isArray(mentees) || mentees.length === 0) return '';
    
    return mentees
      .map(mentee => getUserName(mentee))
      .filter(name => name && name !== 'Loading...')
      .join(', ');
  };

  // Load column preferences
  const loadColumnPreferences = async () => {
    try {
      const preferences =
        await ColumnPreferencesService.getColumnPreferences("workshops");
      setColumnPreferences(preferences);

      // Create column definitions with preferences
      const defaultColumnDefs = [
        { key: "title", name: "Workshop Title", width: "w-96 min-w-96" },
        { key: "status", name: "Status", width: "w-24" },
        { key: "progress", name: "Progress", width: "w-32" },
        { key: "startupDate", name: "Startup Date", width: "w-32" },
        { key: "deadline", name: "Deadline", width: "w-32" },
        { key: "department", name: "Department", width: "w-32" },
        { key: "mentor", name: "Mentor", width: "w-32" },
        { key: "mentees", name: "Mentees", width: "w-32" },
        { key: "sqdcmCategory", name: "SQDCM Category", width: "w-32" },
        { key: "kpi", name: "KPI", width: "w-32" },
        { key: "initialCondition", name: "Initial Condition", width: "w-32" },
        { key: "currentCondition", name: "Current Condition", width: "w-32" },
        { key: "targetCondition", name: "Target Condition", width: "w-32" },
        { key: "estimatedSavings", name: "Estimated Savings", width: "w-36" },
        { key: "releasedSavings", name: "Released Savings", width: "w-36" },
        {
          key: "savingsRealizationRate",
          name: "Realization Rate",
          width: "w-32",
        },
        // New fields from Excel analysis
        { key: "productLine", name: "Product Line", width: "w-32" },
        { key: "alvId", name: "ALV ID", width: "w-24" },
        { key: "location", name: "Location", width: "w-32" },
        { key: "category", name: "Category", width: "w-40" },
        { key: "ebitBridgeLever", name: "EBIT Bridge Lever", width: "w-40" },
        { key: "impactType", name: "Impact Type", width: "w-32" },
        { key: "workstream", name: "Workstream", width: "w-32" },
        { key: "apsMaturity", name: "APS Maturity", width: "w-32" },
        { key: "wsArea", name: "WS Area", width: "w-36" },
        { key: "lGate", name: "L-Gate", width: "w-24" },
        { key: "priority", name: "Priority", width: "w-24" },
        { key: "description", name: "Description", width: "w-96 min-w-96" },
        { key: "actions", name: "Actions", width: "w-32" },
      ];

      const definitions = ColumnPreferencesService.mergeWithPreferences(
        defaultColumnDefs,
        preferences
      );
      setColumnDefinitions(definitions);
    } catch (error) {
      console.error("Failed to load column preferences:", error);
      // Fallback to default definitions
      setColumnDefinitions([]);
    }
  };

  // Handle search input with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== filters.search) {
        setFilters({ search: searchInput || undefined });
        fetchWorkshops();
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchInput, filters.search, setFilters, fetchWorkshops]);

  // Handle filter changes
  const handleFilterChange = (key: string, value: any) => {
    setFilters({ [key]: value || undefined });
    fetchWorkshops();
  };

  // Handle pagination
  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    fetchWorkshops();
  };

  // Handle page size change
  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    fetchWorkshops();
  };

  // Clear all filters
  const handleClearFilters = () => {
    setSearchInput("");
    clearFilters();
    fetchWorkshops();
  };

  // Get status badge classes
  const getStatusBadge = (status: string) => {
    const baseClasses =
      "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium";
    const statusClasses = WorkshopService.getStatusColor(status);
    return `${baseClasses} ${statusClasses}`;
  };

  // Get progress bar color
  const getProgressBarColor = (progress: number | null) => {
    return WorkshopService.getProgressColor(progress);
  };

  // Save column visibility to localStorage
  useEffect(() => {
    localStorage.setItem(
      "workshopTableColumns",
      JSON.stringify(visibleColumns)
    );
  }, [visibleColumns]);

  // Toggle column visibility
  const toggleColumn = (columnKey: keyof ColumnVisibility) => {
    setVisibleColumns((prev) => ({
      ...prev,
      [columnKey]: !prev[columnKey],
    }));
  };

  // Reset columns to default
  const resetColumns = () => {
    setVisibleColumns(defaultColumns);
  };

  // Get display name for a column (uses custom name if available)
  const getColumnDisplayName = (columnKey: string): string => {
    const definition = columnDefinitions.find((def) => def.key === columnKey);
    return definition?.displayName || columnKey;
  };

  // Check if column is customized
  const isColumnCustomized = (columnKey: string): boolean => {
    const definition = columnDefinitions.find((def) => def.key === columnKey);
    return definition?.isCustomized || false;
  };

  // Get default name for a column
  const getColumnDefaultName = (columnKey: string): string => {
    const definition = columnDefinitions.find((def) => def.key === columnKey);
    return definition?.defaultName || columnKey;
  };

  // Handle saving a custom column name
  const handleSaveColumnName = async (
    columnKey: string,
    newName: string
  ): Promise<void> => {
    try {
      await ColumnPreferencesService.updateSingleColumnPreference(
        "workshops",
        columnKey,
        newName
      );
      // Reload preferences to update the UI
      await loadColumnPreferences();
    } catch (error) {
      console.error("Failed to save column name:", error);
      throw error;
    }
  };

  // Handle resetting a column name to default
  const handleResetColumnName = async (columnKey: string): Promise<void> => {
    try {
      await ColumnPreferencesService.resetColumnPreference(
        "workshops",
        columnKey
      );
      // Reload preferences to update the UI
      await loadColumnPreferences();
    } catch (error) {
      console.error("Failed to reset column name:", error);
      throw error;
    }
  };

  // Handle bulk save from modal
  const handleBulkSavePreferences = async (
    preferences: Record<string, string>
  ): Promise<void> => {
    try {
      await ColumnPreferencesService.updateColumnPreferences(
        "workshops",
        preferences
      );
      // Reload preferences to update the UI
      await loadColumnPreferences();
    } catch (error) {
      console.error("Failed to save bulk preferences:", error);
      throw error;
    }
  };

  // Handle reset all preferences
  const handleResetAllPreferences = async (): Promise<void> => {
    try {
      await ColumnPreferencesService.resetAllColumnPreferences("workshops");
      // Reload preferences to update the UI
      await loadColumnPreferences();
    } catch (error) {
      console.error("Failed to reset all preferences:", error);
      throw error;
    }
  };

  // Handle updating workshop field
  const handleUpdateWorkshopField = async (
    workshopId: number,
    fieldName: string,
    value: any
  ): Promise<void> => {
    try {
      await WorkshopService.updateField(workshopId, fieldName, value);
      // Refresh the workshops list to show updated data
      fetchWorkshops();
    } catch (error) {
      console.error("Failed to update workshop field:", error);
      throw error;
    }
  };

  // Handle updating multiple workshop fields at once (for currency fields)
  const handleUpdateWorkshopFields = async (
    workshopId: number,
    updates: Record<string, any>
  ): Promise<void> => {
    try {
      await WorkshopService.updateWorkshop(workshopId, updates);
      // Refresh the workshops list to show updated data
      fetchWorkshops();
    } catch (error) {
      console.error("Failed to update workshop fields:", error);
      throw error;
    }
  };

  // Column definitions for the menu - organized logically by groups
  const columns: Array<{
    key: keyof ColumnVisibility;
    label: string;
    required?: boolean;
  }> = [
    // Core Workshop Info
    { key: "title", label: getColumnDisplayName("title"), required: true },
    { key: "status", label: getColumnDisplayName("status") },
    { key: "progress", label: getColumnDisplayName("progress") },
    { key: "priority", label: getColumnDisplayName("priority") },
    { key: "description", label: getColumnDisplayName("description") },

    // Timeline & Dates
    { key: "startupDate", label: getColumnDisplayName("startupDate") },
    { key: "deadline", label: getColumnDisplayName("deadline") },
    { key: "lGate", label: getColumnDisplayName("lGate") },

    // Organization & People
    { key: "department", label: getColumnDisplayName("department") },
    { key: "mentor", label: getColumnDisplayName("mentor") },
    { key: "mentees", label: getColumnDisplayName("mentees") },
    { key: "productLine", label: getColumnDisplayName("productLine") },
    { key: "alvId", label: getColumnDisplayName("alvId") },
    { key: "location", label: getColumnDisplayName("location") },
    { key: "wsArea", label: getColumnDisplayName("wsArea") },

    // Categories & Classification
    { key: "sqdcmCategory", label: getColumnDisplayName("sqdcmCategory") },
    { key: "kpi", label: getColumnDisplayName("kpi") },
    { key: "category", label: getColumnDisplayName("category") },
    { key: "ebitBridgeLever", label: getColumnDisplayName("ebitBridgeLever") },
    { key: "impactType", label: getColumnDisplayName("impactType") },
    { key: "workstream", label: getColumnDisplayName("workstream") },
    { key: "apsMaturity", label: getColumnDisplayName("apsMaturity") },

    // Conditions & Metrics
    {
      key: "initialCondition",
      label: getColumnDisplayName("initialCondition"),
    },
    {
      key: "currentCondition",
      label: getColumnDisplayName("currentCondition"),
    },
    { key: "targetCondition", label: getColumnDisplayName("targetCondition") },

    // Financial Data
    {
      key: "estimatedSavings",
      label: getColumnDisplayName("estimatedSavings"),
    },
    { key: "releasedSavings", label: getColumnDisplayName("releasedSavings") },
    {
      key: "savingsRealizationRate",
      label: getColumnDisplayName("savingsRealizationRate"),
    },

    // Actions
    { key: "actions", label: getColumnDisplayName("actions"), required: true },
  ];

  // Handle workshop deletion
  const handleDeleteWorkshop = (workshopId: number, workshopTitle: string) => {
    setDeleteAlert({
      isOpen: true,
      workshopId,
      workshopTitle,
    });
  };

  // Confirm workshop deletion
  const confirmDeleteWorkshop = async () => {
    if (!deleteAlert.workshopId) return;

    try {
      await WorkshopService.deleteWorkshop(deleteAlert.workshopId);
      fetchWorkshops();
      // Close the alert
      setDeleteAlert({ isOpen: false, workshopId: null, workshopTitle: "" });
    } catch (error) {
      console.error("Error deleting workshop:", error);
      // Close the alert even on error (error handling is done by the service/store)
      setDeleteAlert({ isOpen: false, workshopId: null, workshopTitle: "" });
    }
  };

  // Cancel workshop deletion
  const cancelDeleteWorkshop = () => {
    setDeleteAlert({ isOpen: false, workshopId: null, workshopTitle: "" });
  };

  // Close column menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const columnMenuContainer = target.closest(".column-menu-container");

      if (showColumnMenu && !columnMenuContainer) {
        setShowColumnMenu(false);
      }
    };

    if (showColumnMenu) {
      // Add a small delay to prevent immediate closing when opening
      setTimeout(() => {
        document.addEventListener("click", handleClickOutside);
      }, 100);
    }

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [showColumnMenu]);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Workshops</h1>
              <p className="mt-1 text-sm text-gray-600">
                Manage and track improvement workshops
              </p>
            </div>
            {isAdmin && (
              <Link
                to="/workshops/new"
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              >
                New Workshop
              </Link>
            )}
          </div>
        </div>
      </div>

      <div className="px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <div className="bg-white shadow rounded-lg mb-6">
          <div className="p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">Filters</h2>

              {/* Search */}
            <div className="mb-4">
              <label
                htmlFor="search"
                className="block text-sm font-medium text-gray-700"
              >
                  Search
                </label>
                  <input
                    type="text"
                    id="search"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search by title, mentor, or description..."
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
              />
              </div>

            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {/* Date Range Filter */}
              <div className="col-span-1 sm:col-span-2 lg:col-span-4 mb-2">
                <DateRangeFilter
                  label="Filter by Deadline"
                  startDate={filters.deadline_from}
                  endDate={filters.deadline_to}
                  onStartDateChange={(date) => handleFilterChange("deadline_from", date)}
                  onEndDateChange={(date) => handleFilterChange("deadline_to", date)}
                  onClear={() => setFilters({ deadline_from: undefined, deadline_to: undefined })}
                  className="w-full sm:w-auto"
                />
              </div>

              {/* Status Filter */}
              <div>
                <label
                  htmlFor="status"
                  className="block text-sm font-medium text-gray-700"
                >
                  Status
                </label>
                <select
                  id="status"
                  value={filters.status || ""}
                  onChange={(e) => handleFilterChange("status", e.target.value)}
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm rounded-md"
                >
                  <option value="">All Statuses</option>
                  {(dropdownOptions.status || []).map((option) => (
                    <option key={option.id} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Department Filter */}
              <div>
                <label
                  htmlFor="department"
                  className="block text-sm font-medium text-gray-700"
                >
                  Department
                </label>
                <select
                  id="department"
                  value={filters.department || ""}
                  onChange={(e) =>
                    handleFilterChange(
                      "department",
                      e.target.value ? parseInt(e.target.value) : undefined
                    )
                  }
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm rounded-md"
                >
                  <option value="">All Departments</option>
                  {departments.map((dept) => (
                    <option key={dept.id} value={dept.id}>
                      {dept.name} ({dept.code})
                    </option>
                  ))}
                </select>
              </div>

              {/* Sort Order */}
              <div>
                <label
                  htmlFor="ordering"
                  className="block text-sm font-medium text-gray-700"
                >
                  Sort By
                </label>
                <select
                  id="ordering"
                  value={filters.ordering || "-created_at"}
                  onChange={(e) =>
                    handleFilterChange("ordering", e.target.value)
                  }
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary focus:border-primary sm:text-sm rounded-md"
                >
                  <option value="-created_at">Newest First</option>
                  <option value="created_at">Oldest First</option>
                  <option value="-progress_rate">Progress (High to Low)</option>
                  <option value="progress_rate">Progress (Low to High)</option>
                  <option value="deadline">Deadline (Earliest)</option>
                  <option value="-deadline">Deadline (Latest)</option>
                  <option value="-estimated_savings_per_year">
                    Savings (High to Low)
                  </option>
                </select>
              </div>
            </div>

            {/* Clear Filters and Column Settings */}
            <div className="mt-4 flex justify-between items-center">
              <button
                onClick={handleClearFilters}
                className="text-sm text-primary hover:text-primary-700"
              >
                Clear all filters
              </button>

              {/* Column Visibility Menu */}
              <div className="flex items-center space-x-4">
              <div className="text-sm text-gray-500">
                  {totalCount} workshop{totalCount !== 1 ? "s" : ""} found
              </div>

                <div className="relative column-menu-container">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowColumnMenu(!showColumnMenu);
                    }}
                    className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                  >
                    <Settings className="h-4 w-4" />
                    <span>Columns</span>
                  </button>

                  {/* Column Menu Dropdown */}
                  {showColumnMenu && (
                    <div
                      className="absolute right-0 mt-2 w-64 bg-white rounded-md shadow-lg border border-gray-200 z-50"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="p-4">
                        <div className="flex items-center justify-between mb-3">
                          <h3 className="text-sm font-medium text-gray-900">
                            Show Columns
                          </h3>
                          <div className="flex space-x-2">
                            <button
                              onClick={() => {
                                setShowPreferencesModal(true);
                                setShowColumnMenu(false);
                              }}
                              className="text-xs text-blue-600 hover:text-blue-800"
                            >
                              Edit Names
                            </button>
                            <button
                              onClick={resetColumns}
                              className="text-xs text-primary hover:text-primary-700"
                            >
                              Reset
                            </button>
            </div>
          </div>
                        <div className="space-y-2">
                          {/* Core Workshop Info header */}
                          <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                            Core Workshop Info
                          </div>
                          {columns.map((column, index) => {
                            // Determine if we need a separator before this column
                            const needsSeparator =
                              column.key === "startupDate" || // Timeline section
                              column.key === "department" || // Organization section
                              column.key === "sqdcmCategory" || // Categories section
                              column.key === "initialCondition" || // Conditions section
                              column.key === "estimatedSavings" || // Financial section
                              column.key === "actions"; // Actions section

                            return (
                              <div key={column.key}>
                                {needsSeparator && index > 0 && (
                                  <div className="border-t border-gray-200 my-2 pt-2">
                                    <div className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
                                      {column.key === "startupDate" &&
                                        "Timeline & Dates"}
                                      {column.key === "department" &&
                                        "Organization & People"}
                                      {column.key === "sqdcmCategory" &&
                                        "Categories & Classification"}
                                      {column.key === "initialCondition" &&
                                        "Conditions & Metrics"}
                                      {column.key === "estimatedSavings" &&
                                        "Financial Data"}
                                      {column.key === "actions" && "Actions"}
                                    </div>
                                  </div>
                                )}
                                <label className="flex items-center cursor-pointer hover:bg-gray-50 p-1 rounded">
                                  <input
                                    type="checkbox"
                                    checked={visibleColumns[column.key]}
                                    onChange={(e) => {
                                      e.stopPropagation();
                                      toggleColumn(column.key);
                                    }}
                                    disabled={column.required}
                                    className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                                  />
                                  <span
                                    className={`ml-2 text-sm ${column.required ? "text-gray-400" : "text-gray-700"}`}
                                  >
                                    {column.label}
                                    {column.required && " (required)"}
                                  </span>
                                </label>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-red-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="bg-white shadow rounded-lg p-6">
            <div className="animate-pulse">
              <div className="space-y-4">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="h-16 bg-gray-200 rounded"></div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Workshops Table */}
        {!isLoading && (
          <div className="bg-white shadow overflow-hidden sm:rounded-lg">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {/* Core Workshop Info */}
                    {visibleColumns.title && (
                      <th className="px-4 py-3 text-left w-96 min-w-96">
                        <EditableColumnHeader
                          columnKey="title"
                          displayName={getColumnDisplayName("title")}
                          defaultName={getColumnDefaultName("title")}
                          isCustomized={isColumnCustomized("title")}
                          onSave={handleSaveColumnName}
                          onReset={handleResetColumnName}
                        />
                    </th>
                    )}
                    {visibleColumns.status && (
                      <th className="px-4 py-3 text-left w-20">
                        <EditableColumnHeader
                          columnKey="status"
                          displayName={getColumnDisplayName("status")}
                          defaultName={getColumnDefaultName("status")}
                          isCustomized={isColumnCustomized("status")}
                          onSave={handleSaveColumnName}
                          onReset={handleResetColumnName}
                        />
                    </th>
                    )}
                    {visibleColumns.progress && (
                      <th className="px-4 py-3 text-left w-24">
                        <EditableColumnHeader
                          columnKey="progress"
                          displayName={getColumnDisplayName("progress")}
                          defaultName={getColumnDefaultName("progress")}
                          isCustomized={isColumnCustomized("progress")}
                          onSave={handleSaveColumnName}
                          onReset={handleResetColumnName}
                        />
                    </th>
                    )}

                    {/* Timeline & Dates */}
                    {visibleColumns.startupDate && (
                      <th className="px-4 py-3 text-left w-28">
                        <EditableColumnHeader
                          columnKey="startupDate"
                          displayName={getColumnDisplayName("startupDate")}
                          defaultName={getColumnDefaultName("startupDate")}
                          isCustomized={isColumnCustomized("startupDate")}
                          onSave={handleSaveColumnName}
                          onReset={handleResetColumnName}
                        />
                    </th>
                    )}
                    {visibleColumns.deadline && (
                      <th className="px-4 py-3 text-left w-28">
                        <EditableColumnHeader
                          columnKey="deadline"
                          displayName={getColumnDisplayName("deadline")}
                          defaultName={getColumnDefaultName("deadline")}
                          isCustomized={isColumnCustomized("deadline")}
                          onSave={handleSaveColumnName}
                          onReset={handleResetColumnName}
                        />
                    </th>
                    )}

                    {/* Organization & People */}
                    {visibleColumns.department && (
                      <th className="px-4 py-3 text-left w-28">
                        <EditableColumnHeader
                          columnKey="department"
                          displayName={getColumnDisplayName("department")}
                          defaultName={getColumnDefaultName("department")}
                          isCustomized={isColumnCustomized("department")}
                          onSave={handleSaveColumnName}
                          onReset={handleResetColumnName}
                        />
                    </th>
                    )}
                    {visibleColumns.mentor && (
                      <th className="px-4 py-3 text-left w-32">
                        <EditableColumnHeader
                          columnKey="mentor"
                          displayName={getColumnDisplayName("mentor")}
                          defaultName={getColumnDefaultName("mentor")}
                          isCustomized={isColumnCustomized("mentor")}
                          onSave={handleSaveColumnName}
                          onReset={handleResetColumnName}
                        />
                    </th>
                    )}
                    {visibleColumns.mentees && (
                      <th className="px-4 py-3 text-left w-24">
                        <EditableColumnHeader
                          columnKey="mentees"
                          displayName={getColumnDisplayName("mentees")}
                          defaultName={getColumnDefaultName("mentees")}
                          isCustomized={isColumnCustomized("mentees")}
                          onSave={handleSaveColumnName}
                          onReset={handleResetColumnName}
                        />
                      </th>
                    )}

                    {/* Categories & Classification */}
                    {visibleColumns.sqdcmCategory && (
                      <th className="px-4 py-3 text-left w-24">
                        <EditableColumnHeader
                          columnKey="sqdcmCategory"
                          displayName={getColumnDisplayName("sqdcmCategory")}
                          defaultName={getColumnDefaultName("sqdcmCategory")}
                          isCustomized={isColumnCustomized("sqdcmCategory")}
                          onSave={handleSaveColumnName}
                          onReset={handleResetColumnName}
                        />
                      </th>
                    )}
                    {visibleColumns.kpi && (
                      <th className="px-4 py-3 text-left w-24">
                        <EditableColumnHeader
                          columnKey="kpi"
                          displayName={getColumnDisplayName("kpi")}
                          defaultName={getColumnDefaultName("kpi")}
                          isCustomized={isColumnCustomized("kpi")}
                          onSave={handleSaveColumnName}
                          onReset={handleResetColumnName}
                        />
                      </th>
                    )}

                    {/* Conditions & Metrics */}
                    {visibleColumns.initialCondition && (
                      <th className="px-4 py-3 text-left w-20">
                        <EditableColumnHeader
                          columnKey="initialCondition"
                          displayName={getColumnDisplayName("initialCondition")}
                          defaultName={getColumnDefaultName("initialCondition")}
                          isCustomized={isColumnCustomized("initialCondition")}
                          onSave={handleSaveColumnName}
                          onReset={handleResetColumnName}
                        />
                      </th>
                    )}
                    {visibleColumns.currentCondition && (
                      <th className="px-4 py-3 text-left w-20">
                        <EditableColumnHeader
                          columnKey="currentCondition"
                          displayName={getColumnDisplayName("currentCondition")}
                          defaultName={getColumnDefaultName("currentCondition")}
                          isCustomized={isColumnCustomized("currentCondition")}
                          onSave={handleSaveColumnName}
                          onReset={handleResetColumnName}
                        />
                      </th>
                    )}
                    {visibleColumns.targetCondition && (
                      <th className="px-4 py-3 text-left w-20">
                        <EditableColumnHeader
                          columnKey="targetCondition"
                          displayName={getColumnDisplayName("targetCondition")}
                          defaultName={getColumnDefaultName("targetCondition")}
                          isCustomized={isColumnCustomized("targetCondition")}
                          onSave={handleSaveColumnName}
                          onReset={handleResetColumnName}
                        />
                      </th>
                    )}

                    {/* Financial Data */}
                    {visibleColumns.estimatedSavings && (
                      <th className="px-4 py-3 text-left w-24">
                        <EditableColumnHeader
                          columnKey="estimatedSavings"
                          displayName={getColumnDisplayName("estimatedSavings")}
                          defaultName={getColumnDefaultName("estimatedSavings")}
                          isCustomized={isColumnCustomized("estimatedSavings")}
                          onSave={handleSaveColumnName}
                          onReset={handleResetColumnName}
                        />
                      </th>
                    )}
                    {visibleColumns.releasedSavings && (
                      <th className="px-4 py-3 text-left w-24">
                        <EditableColumnHeader
                          columnKey="releasedSavings"
                          displayName={getColumnDisplayName("releasedSavings")}
                          defaultName={getColumnDefaultName("releasedSavings")}
                          isCustomized={isColumnCustomized("releasedSavings")}
                          onSave={handleSaveColumnName}
                          onReset={handleResetColumnName}
                        />
                      </th>
                    )}
                    {visibleColumns.savingsRealizationRate && (
                      <th className="px-4 py-3 text-left w-20">
                        <EditableColumnHeader
                          columnKey="savingsRealizationRate"
                          displayName={getColumnDisplayName(
                            "savingsRealizationRate"
                          )}
                          defaultName={getColumnDefaultName(
                            "savingsRealizationRate"
                          )}
                          isCustomized={isColumnCustomized(
                            "savingsRealizationRate"
                          )}
                          onSave={handleSaveColumnName}
                          onReset={handleResetColumnName}
                        />
                      </th>
                    )}

                    {/* New Excel Fields - Product & Location */}
                    {visibleColumns.productLine && (
                      <th className="px-4 py-3 text-left w-32">
                        <EditableColumnHeader
                          columnKey="productLine"
                          displayName={getColumnDisplayName("productLine")}
                          defaultName={getColumnDefaultName("productLine")}
                          isCustomized={isColumnCustomized("productLine")}
                          onSave={handleSaveColumnName}
                          onReset={handleResetColumnName}
                        />
                      </th>
                    )}
                    {visibleColumns.alvId && (
                      <th className="px-4 py-3 text-left w-24">
                        <EditableColumnHeader
                          columnKey="alvId"
                          displayName={getColumnDisplayName("alvId")}
                          defaultName={getColumnDefaultName("alvId")}
                          isCustomized={isColumnCustomized("alvId")}
                          onSave={handleSaveColumnName}
                          onReset={handleResetColumnName}
                        />
                      </th>
                    )}
                    {visibleColumns.location && (
                      <th className="px-4 py-3 text-left w-32">
                        <EditableColumnHeader
                          columnKey="location"
                          displayName={getColumnDisplayName("location")}
                          defaultName={getColumnDefaultName("location")}
                          isCustomized={isColumnCustomized("location")}
                          onSave={handleSaveColumnName}
                          onReset={handleResetColumnName}
                        />
                      </th>
                    )}
                    {visibleColumns.wsArea && (
                      <th className="px-4 py-3 text-left w-36">
                        <EditableColumnHeader
                          columnKey="wsArea"
                          displayName={getColumnDisplayName("wsArea")}
                          defaultName={getColumnDefaultName("wsArea")}
                          isCustomized={isColumnCustomized("wsArea")}
                          onSave={handleSaveColumnName}
                          onReset={handleResetColumnName}
                        />
                      </th>
                    )}

                    {/* New Excel Fields - Business Classification */}
                    {visibleColumns.category && (
                      <th className="px-4 py-3 text-left w-40">
                        <EditableColumnHeader
                          columnKey="category"
                          displayName={getColumnDisplayName("category")}
                          defaultName={getColumnDefaultName("category")}
                          isCustomized={isColumnCustomized("category")}
                          onSave={handleSaveColumnName}
                          onReset={handleResetColumnName}
                        />
                      </th>
                    )}
                    {visibleColumns.ebitBridgeLever && (
                      <th className="px-4 py-3 text-left w-40">
                        <EditableColumnHeader
                          columnKey="ebitBridgeLever"
                          displayName={getColumnDisplayName("ebitBridgeLever")}
                          defaultName={getColumnDefaultName("ebitBridgeLever")}
                          isCustomized={isColumnCustomized("ebitBridgeLever")}
                          onSave={handleSaveColumnName}
                          onReset={handleResetColumnName}
                        />
                      </th>
                    )}
                    {visibleColumns.impactType && (
                      <th className="px-4 py-3 text-left w-32">
                        <EditableColumnHeader
                          columnKey="impactType"
                          displayName={getColumnDisplayName("impactType")}
                          defaultName={getColumnDefaultName("impactType")}
                          isCustomized={isColumnCustomized("impactType")}
                          onSave={handleSaveColumnName}
                          onReset={handleResetColumnName}
                        />
                      </th>
                    )}
                    {visibleColumns.workstream && (
                      <th className="px-4 py-3 text-left w-32">
                        <EditableColumnHeader
                          columnKey="workstream"
                          displayName={getColumnDisplayName("workstream")}
                          defaultName={getColumnDefaultName("workstream")}
                          isCustomized={isColumnCustomized("workstream")}
                          onSave={handleSaveColumnName}
                          onReset={handleResetColumnName}
                        />
                      </th>
                    )}

                    {/* New Excel Fields - Process & Priority */}
                    {visibleColumns.apsMaturity && (
                      <th className="px-4 py-3 text-left w-32">
                        <EditableColumnHeader
                          columnKey="apsMaturity"
                          displayName={getColumnDisplayName("apsMaturity")}
                          defaultName={getColumnDefaultName("apsMaturity")}
                          isCustomized={isColumnCustomized("apsMaturity")}
                          onSave={handleSaveColumnName}
                          onReset={handleResetColumnName}
                        />
                      </th>
                    )}
                    {visibleColumns.lGate && (
                      <th className="px-4 py-3 text-left w-24">
                        <EditableColumnHeader
                          columnKey="lGate"
                          displayName={getColumnDisplayName("lGate")}
                          defaultName={getColumnDefaultName("lGate")}
                          isCustomized={isColumnCustomized("lGate")}
                          onSave={handleSaveColumnName}
                          onReset={handleResetColumnName}
                        />
                      </th>
                    )}
                    {visibleColumns.priority && (
                      <th className="px-4 py-3 text-left w-24">
                        <EditableColumnHeader
                          columnKey="priority"
                          displayName={getColumnDisplayName("priority")}
                          defaultName={getColumnDefaultName("priority")}
                          isCustomized={isColumnCustomized("priority")}
                          onSave={handleSaveColumnName}
                          onReset={handleResetColumnName}
                        />
                      </th>
                    )}
                    {visibleColumns.description && (
                      <th className="px-4 py-3 text-left w-96 min-w-96">
                        <EditableColumnHeader
                          columnKey="description"
                          displayName={getColumnDisplayName("description")}
                          defaultName={getColumnDefaultName("description")}
                          isCustomized={isColumnCustomized("description")}
                          onSave={handleSaveColumnName}
                          onReset={handleResetColumnName}
                        />
                      </th>
                    )}

                    {/* Actions */}
                    {visibleColumns.actions && (
                      <th className="px-4 py-3 text-right w-20">
                        <EditableColumnHeader
                          columnKey="actions"
                          displayName={getColumnDisplayName("actions")}
                          defaultName={getColumnDefaultName("actions")}
                          isCustomized={isColumnCustomized("actions")}
                          onSave={handleSaveColumnName}
                          onReset={handleResetColumnName}
                        />
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {workshops.map((workshop) => {
                    const deadlineInfo = WorkshopService.getDaysUntilDeadline(
                      workshop.deadline
                    );
                    
                    return (
                      <WorkshopEditabilityProvider
                        key={workshop.id}
                        canEdit={workshop.can_edit === true}
                      >
                      <tr className="hover:bg-gray-50">
                        {/* Core Workshop Info */}
                        {visibleColumns.title && (
                          <td className="px-4 py-3 min-w-64">
                            <div className="flex flex-col">
                              <div className="text-sm font-medium text-gray-900 pr-2">
                                <EditableCell
                                  value={workshop.title}
                                  workshopId={workshop.id}
                                  fieldName="title"
                                  fieldType="text"
                                  onSave={handleUpdateWorkshopField}
                                  placeholder="Workshop title..."
                                  renderDisplayValue={(val) => (
                                    <Link
                                      to={`/workshops/${workshop.id}`}
                                      className="text-gray-900 hover:text-blue-600 hover:underline"
                                    >
                                      {val || "Workshop title..."}
                                    </Link>
                                  )}
                                />
                            </div>
                              <div className="text-xs text-gray-500">
                                #
                                {WorkshopService.formatWorkshopNumber(
                                  workshop.workshop_number
                                )}
                            </div>
                          </div>
                        </td>
                        )}
                        {visibleColumns.status && (
                          <td className="px-4 py-3 whitespace-nowrap">
                            <EditableCell
                              value={workshop.status}
                              workshopId={workshop.id}
                              fieldName="status"
                              fieldType="select"
                              selectOptions={getSelectOptions('status')}
                              onSave={handleUpdateWorkshopField}
                              displayFormatter={(value) =>
                                WorkshopService.formatStatus(value)
                              }
                            />
                        </td>
                        )}
                        {visibleColumns.progress && (
                          <td className="px-4 py-3 whitespace-nowrap">
                          <div className="flex items-center">
                              <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                              <div
                                className={`h-2 rounded-full ${getProgressBarColor(workshop.progress_rate)}`}
                                  style={{
                                    width: `${Math.min(workshop.progress_rate || 0, 100)}%`,
                                  }}
                              ></div>
                            </div>
                              <div className="min-w-0">
                                <EditableCell
                                  value={workshop.progress_rate}
                                  workshopId={workshop.id}
                                  fieldName="progress_rate"
                                  fieldType="number"
                                  onSave={handleUpdateWorkshopField}
                                  placeholder="0"
                                  displayFormatter={(value) =>
                                    WorkshopService.formatProgress(value)
                                  }
                                  className="text-xs"
                                  disabled={true}
                                />
                              </div>
                          </div>
                        </td>
                        )}

                        {/* Timeline & Dates */}
                        {visibleColumns.startupDate && (
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                            <EditableCell
                              value={workshop.startup_date}
                              workshopId={workshop.id}
                              fieldName="startup_date"
                              fieldType="date"
                              onSave={handleUpdateWorkshopField}
                              placeholder="No start date"
                              displayFormatter={(value) =>
                                WorkshopService.formatDate(value)
                              }
                            />
                        </td>
                        )}
                        {visibleColumns.deadline && (
                          <td className="px-4 py-3 whitespace-nowrap">
                            <div className="flex flex-col">
                              <EditableCell
                                value={workshop.deadline}
                                workshopId={workshop.id}
                                fieldName="deadline"
                                fieldType="date"
                                onSave={handleUpdateWorkshopField}
                                placeholder="No deadline set"
                                displayFormatter={(value) =>
                                  WorkshopService.formatDate(value)
                                }
                              />
                              {deadlineInfo &&
                                ![
                                  "fully_implemented",
                                  "closed",
                                  "rejected",
                                ].includes(workshop.status) && (
                                  <div
                                    className={`text-xs ${deadlineInfo.isOverdue ? "text-red-600" : "text-gray-500"}`}
                                  >
                                    {deadlineInfo.isOverdue
                                      ? "Overdue by"
                                      : "Due in"}{" "}
                                    {deadlineInfo.days}d
                          </div>
                                )}
                              {workshop.status === "fully_implemented" && (
                                <div className="text-xs text-green-600">
                                  Fully implemented
                          </div>
                              )}
                          </div>
                          </td>
                        )}

                        {/* Organization & People */}
                        {visibleColumns.department && (
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 truncate">
                            <EditableCell
                              value={workshop.department_info?.id || null}
                              workshopId={workshop.id}
                              fieldName="department"
                              fieldType="select"
                              selectOptions={[
                                { value: "", label: "Unassigned" },
                                ...departments.map((dept) => ({
                                  value: dept.id.toString(),
                                  label: dept.name,
                                })),
                              ]}
                              onSave={handleUpdateWorkshopField}
                              displayFormatter={(value) => {
                                if (!value) return "Unassigned";
                                const dept = departments.find(
                                  (d) => d.id.toString() === value.toString()
                                );
                                return dept?.name || "Unassigned";
                              }}
                            />
                        </td>
                        )}
                        {visibleColumns.mentor && (
                          <td className="px-4 py-3 text-sm text-gray-900">
                            <EditableCell
                              value={workshop.mentor_display_name || getUserName(workshop.mentor || '')}
                              workshopId={workshop.id}
                              fieldName="mentor"
                              fieldType="user-select"
                              users={users}
                              onSave={handleUpdateWorkshopField}
                              placeholder="No mentor assigned"
                            />
                          </td>
                        )}
                        {visibleColumns.mentees && (
                          <td className="px-4 py-3 text-sm text-gray-900">
                            <EditableCell
                              value={formatMentees(workshop.mentees, workshop.mentees_display_names)}
                              workshopId={workshop.id}
                              fieldName="mentees"
                              fieldType="user-multi-select"
                              users={users}
                              onSave={handleUpdateWorkshopField}
                              placeholder="No mentees assigned"
                            />
                          </td>
                        )}

                        {/* Categories & Classification */}
                        {visibleColumns.sqdcmCategory && (
                          <td className="px-4 py-3 text-sm text-gray-900">
                            <EditableCell
                              value={workshop.sqdcm_category}
                              workshopId={workshop.id}
                              fieldName="sqdcm_category"
                              fieldType="select"
                              selectOptions={[
                                { value: "", label: "N/A" },
                                ...getSelectOptions('sqdcm_category')
                              ]}
                              onSave={handleUpdateWorkshopField}
                              displayFormatter={getDisplayFormatter('sqdcm_category')}
                              placeholder="N/A"
                            />
                          </td>
                        )}
                        {visibleColumns.kpi && (
                          <td className="px-4 py-3 text-sm text-gray-900">
                            <EditableCell
                              value={workshop.kpi}
                              workshopId={workshop.id}
                              fieldName="kpi"
                              fieldType="text"
                              onSave={handleUpdateWorkshopField}
                              placeholder="N/A"
                            />
                          </td>
                        )}

                        {/* Conditions & Metrics */}
                        {visibleColumns.initialCondition && (
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                            <EditableCell
                              value={workshop.initial_condition}
                              workshopId={workshop.id}
                              fieldName="initial_condition"
                              fieldType="number"
                              onSave={handleUpdateWorkshopField}
                              placeholder="N/A"
                            />
                          </td>
                        )}
                        {visibleColumns.currentCondition && (
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                            <EditableCell
                              value={workshop.current_condition}
                              workshopId={workshop.id}
                              fieldName="current_condition"
                              fieldType="number"
                              onSave={handleUpdateWorkshopField}
                              placeholder="N/A"
                            />
                          </td>
                        )}
                        {visibleColumns.targetCondition && (
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                            <EditableCell
                              value={workshop.target_condition}
                              workshopId={workshop.id}
                              fieldName="target_condition"
                              fieldType="number"
                              onSave={handleUpdateWorkshopField}
                              placeholder="N/A"
                            />
                          </td>
                        )}

                        {/* Financial Data */}
                        {visibleColumns.estimatedSavings && (
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                            <EditableCurrencyCell
                              value={workshop.estimated_savings_per_year}
                              unit={
                                workshop.estimated_savings_unit as
                                  | "EUR"
                                  | "KEUR"
                                  | "MEUR"
                              }
                              workshopId={workshop.id}
                              valueFieldName="estimated_savings_per_year"
                              unitFieldName="estimated_savings_unit"
                              onSave={handleUpdateWorkshopFields}
                              placeholder="N/A"
                              displayFormatter={(value, unit) =>
                                WorkshopService.formatCurrencyWithUnit(
                                  value,
                                  unit as "EUR" | "KEUR" | "MEUR"
                                )
                              }
                            />
                          </td>
                        )}
                        {visibleColumns.releasedSavings && (
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                            <EditableCurrencyCell
                              value={workshop.released_savings}
                              unit={
                                workshop.released_savings_unit as
                                  | "EUR"
                                  | "KEUR"
                                  | "MEUR"
                              }
                              workshopId={workshop.id}
                              valueFieldName="released_savings"
                              unitFieldName="released_savings_unit"
                              onSave={handleUpdateWorkshopFields}
                              placeholder="N/A"
                              displayFormatter={(value, unit) =>
                                WorkshopService.formatCurrencyWithUnit(
                                  value,
                                  unit as "EUR" | "KEUR" | "MEUR"
                                )
                              }
                              disabled={true}
                            />
                          </td>
                        )}
                        {visibleColumns.savingsRealizationRate && (
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                            <span className="text-gray-500 text-xs">
                              {workshop.savings_realization_rate
                                ? `${workshop.savings_realization_rate.toFixed(1)}%`
                                : "N/A"}
                              <br />
                              <em>(Auto-calculated)</em>
                            </span>
                          </td>
                        )}

                        {/* New Excel Fields - Product & Location */}
                        {visibleColumns.productLine && (
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                            <EditableCell
                              value={workshop.product_line}
                              workshopId={workshop.id}
                              fieldName="product_line"
                              fieldType="select"
                              selectOptions={getSelectOptions('product_line')}
                              onSave={handleUpdateWorkshopField}
                              displayFormatter={getDisplayFormatter('product_line')}
                              placeholder="Select product line..."
                            />
                          </td>
                        )}
                        {visibleColumns.alvId && (
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                            <EditableCell
                              value={workshop.alv_id}
                              workshopId={workshop.id}
                              fieldName="alv_id"
                              fieldType="select"
                              selectOptions={getSelectOptions('alv_id', true)}
                              onSave={handleUpdateWorkshopField}
                              displayFormatter={getDisplayFormatter('alv_id')}
                              placeholder="Select ALV ID..."
                            />
                          </td>
                        )}
                        {visibleColumns.location && (
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                            <EditableCell
                              value={workshop.location}
                              workshopId={workshop.id}
                              fieldName="location"
                              fieldType="select"
                              selectOptions={getSelectOptions('location')}
                              onSave={handleUpdateWorkshopField}
                              displayFormatter={getDisplayFormatter('location')}
                              placeholder="Select location..."
                            />
                          </td>
                        )}
                        {visibleColumns.wsArea && (
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                            <EditableCell
                              value={workshop.ws_area}
                              workshopId={workshop.id}
                              fieldName="ws_area"
                              fieldType="select"
                              selectOptions={getSelectOptions('ws_area', true)}
                              onSave={handleUpdateWorkshopField}
                              displayFormatter={getDisplayFormatter('ws_area')}
                              placeholder="Select WS Area..."
                            />
                          </td>
                        )}

                        {/* New Excel Fields - Business Classification */}
                        {visibleColumns.category && (
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                            <EditableCell
                              value={workshop.category}
                              workshopId={workshop.id}
                              fieldName="category"
                              fieldType="select"
                              selectOptions={getSelectOptions('category', true)}
                              onSave={handleUpdateWorkshopField}
                              displayFormatter={getDisplayFormatter('category')}
                              placeholder="Select Category..."
                            />
                          </td>
                        )}
                        {visibleColumns.ebitBridgeLever && (
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                            <EditableCell
                              value={workshop.ebit_bridge_lever}
                              workshopId={workshop.id}
                              fieldName="ebit_bridge_lever"
                              fieldType="select"
                              selectOptions={getSelectOptions('ebit_bridge_lever')}
                              onSave={handleUpdateWorkshopField}
                              displayFormatter={getDisplayFormatter('ebit_bridge_lever')}
                              placeholder="Select EBIT lever..."
                            />
                          </td>
                        )}
                        {visibleColumns.impactType && (
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                            <EditableCell
                              value={workshop.impact_type}
                              workshopId={workshop.id}
                              fieldName="impact_type"
                              fieldType="select"
                              selectOptions={getSelectOptions('impact_type')}
                              onSave={handleUpdateWorkshopField}
                              displayFormatter={getDisplayFormatter('impact_type')}
                              placeholder="Select impact type..."
                            />
                          </td>
                        )}
                        {visibleColumns.workstream && (
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                            <EditableCell
                              value={workshop.workstream}
                              workshopId={workshop.id}
                              fieldName="workstream"
                              fieldType="select"
                              selectOptions={getSelectOptions('workstream', true)}
                              onSave={handleUpdateWorkshopField}
                              displayFormatter={getDisplayFormatter('workstream')}
                              placeholder="Select workstream..."
                            />
                          </td>
                        )}

                        {/* New Excel Fields - Process & Priority */}
                        {visibleColumns.apsMaturity && (
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                            <EditableCell
                              value={workshop.aps_maturity}
                              workshopId={workshop.id}
                              fieldName="aps_maturity"
                              fieldType="select"
                              selectOptions={getSelectOptions('aps_maturity')}
                              onSave={handleUpdateWorkshopField}
                              displayFormatter={getDisplayFormatter('aps_maturity')}
                              placeholder="Select APS maturity..."
                            />
                          </td>
                        )}
                        {visibleColumns.lGate && (
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                            <EditableCell
                              value={workshop.l_gate}
                              workshopId={workshop.id}
                              fieldName="l_gate"
                              fieldType="select"
                              selectOptions={getSelectOptions('l_gate')}
                              onSave={handleUpdateWorkshopField}
                              displayFormatter={getDisplayFormatter('l_gate')}
                              placeholder="Select L-Gate..."
                            />
                          </td>
                        )}
                        {visibleColumns.priority && (
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                            <EditableCell
                              value={workshop.priority}
                              workshopId={workshop.id}
                              fieldName="priority"
                              fieldType="select"
                              selectOptions={getSelectOptions('priority')}
                              onSave={handleUpdateWorkshopField}
                              displayFormatter={getDisplayFormatter('priority')}
                              placeholder="Select priority..."
                            />
                          </td>
                        )}
                        {visibleColumns.description && (
                          <td className="px-4 py-3 text-sm text-gray-900 min-w-64">
                            <EditableCell
                              value={workshop.description}
                              workshopId={workshop.id}
                              fieldName="description"
                              fieldType="textarea"
                              onSave={handleUpdateWorkshopField}
                              placeholder="Description..."
                            />
                          </td>
                        )}

                        {visibleColumns.actions && (
                          <td className="px-4 py-3 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex items-center justify-end space-x-1">
                              {/* View */}
                          <Link 
                            to={`/workshops/${workshop.id}`}
                                className="text-gray-600 hover:text-primary transition-colors p-1 rounded"
                                title="View Workshop"
                          >
                                <Eye className="h-4 w-4" />
                          </Link>

                              {/* Edit */}
                          {isAdmin && (
                            <Link 
                              to={`/workshops/${workshop.id}/edit`}
                                  className="text-gray-600 hover:text-primary transition-colors p-1 rounded"
                                  title="Edit Workshop"
                            >
                                  <Edit className="h-4 w-4" />
                            </Link>
                          )}

                              {/* Delete */}
                              {isAdmin && (
                                <button
                                  onClick={() =>
                                    handleDeleteWorkshop(
                                      workshop.id,
                                      workshop.title
                                    )
                                  }
                                  className="text-gray-600 hover:text-red-600 transition-colors p-1 rounded"
                                  title="Delete Workshop"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              )}
                            </div>
                        </td>
                        )}
                      </tr>
                      </WorkshopEditabilityProvider>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* No results message */}
            {workshops.length === 0 && (
              <div className="text-center py-12">
                <svg
                  className="mx-auto h-12 w-12 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                  />
                </svg>
                <h3 className="mt-2 text-sm font-medium text-gray-900">
                  No workshops found
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  {filters.search || filters.status || filters.department
                    ? "Try adjusting your search or filter criteria."
                    : "Get started by creating a new workshop."}
                </p>
                {isAdmin &&
                  !filters.search &&
                  !filters.status &&
                  !filters.department && (
                    <div className="mt-6">
                      <Link
                        to="/workshops/new"
                        className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                      >
                        New Workshop
                      </Link>
              </div>
            )}
              </div>
            )}

            {/* Enhanced Pagination */}
            {workshops.length > 0 && (
              <EnhancedPagination
                currentPage={filters.page || 1}
                totalCount={totalCount}
                pageSize={filters.page_size || 20}
                hasNext={hasNext}
                hasPrevious={hasPrevious}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                pageSizeOptions={[10, 20, 50, 100]}
                showPageSizeSelector={true}
                showPageNumbers={true}
              />
            )}
          </div>
        )}
      </div>

      {/* Delete Confirmation Alert */}
      <Alert
        isOpen={deleteAlert.isOpen}
        onClose={cancelDeleteWorkshop}
        onConfirm={confirmDeleteWorkshop}
        type="error"
        title="Delete Workshop"
        message={`Are you sure you want to delete the workshop "${deleteAlert.workshopTitle}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        showCancel={true}
      />

      {/* Column Preferences Modal */}
      <ColumnPreferencesModal
        isOpen={showPreferencesModal}
        onClose={() => setShowPreferencesModal(false)}
        columnDefinitions={columnDefinitions}
        onSave={handleBulkSavePreferences}
        onResetAll={handleResetAllPreferences}
        tableName="workshops"
      />
    </div>
  );
};

export default Workshops;
